import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { loanAPI, paymentAPI, formatCurrency } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const CreatePayment = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  // Simple loan search/filter
  const [loanQuery, setLoanQuery] = useState({ page: 1, limit: 20 });
  const { data: loansData, isLoading: loansLoading } = useQuery(
    ['loans', loanQuery],
    () => loanAPI.getLoans(loanQuery),
    {
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnReconnect: 'always',
      refetchOnWindowFocus: 'always'
    }
  );

  const [form, setForm] = useState({
    loan_id: '',
    payment_type: 'interest_only',
    amount: '',
    months_paid: 1,
    // one | multiple — controls whether to pay 1 specific month or multiple selected months
    months_option: 'one',
    // selected remaining month keys for interest payments
    selected_month_keys: [],
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    remarks: ''
  });

  const createPayment = useMutation((payload) => paymentAPI.createPayment(payload));

  const loanList = useMemo(() => {
    const arr = loansData?.data?.data?.loans;
    const list = Array.isArray(arr) ? arr : [];
    return list; // show all results from API
  }, [loansData]);

  const selectedLoan = useMemo(() => {
    return loanList.find((l) => String(l.id) === String(form.loan_id));
  }, [loanList, form.loan_id]);

  // Fetch full loan details when a loan is selected (to access monthly calculations/payments)
  const { data: selectedLoanDetails } = useQuery(
    ['loan-details-for-payment', form.loan_id],
    () => loanAPI.getLoan(form.loan_id),
    {
      enabled: !!form.loan_id,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnReconnect: 'always',
      refetchOnWindowFocus: 'always'
    }
  );

  const loanDetails = selectedLoanDetails?.data?.data;

  // Prevent processing payments for closed loans
  const isLoanClosed = useMemo(() => {
    const statusFromList = selectedLoan?.status;
    const statusFromDetails = loanDetails?.loan?.status || loanDetails?.status;
    return String(statusFromList || statusFromDetails || '').toLowerCase() === 'closed';
  }, [selectedLoan, loanDetails]);

  // Utility: format month label from YYYY-MM key (fallback to date if needed)
  const formatMonthLabel = (key, fallbackDate) => {
    if (typeof key === 'string' && key.includes('-')) {
      const [y, m] = key.split('-').map((v) => parseInt(v, 10));
      if (Number.isFinite(y) && Number.isFinite(m)) {
        const date = new Date(y, m - 1, 1);
        return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
      }
    }
    try {
      const d = new Date(fallbackDate);
      return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    } catch (_) {
      return String(key || '');
    }
  };

  // Build monthly unpaid interest list up to the chosen payment month
  const monthlyUnpaid = useMemo(() => {
    if (!loanDetails?.interestCalculations) return [];
    const months = [...loanDetails.interestCalculations].sort((a, b) => new Date(a.calculation_date) - new Date(b.calculation_date));
    const allPayments = [...(loanDetails.payments || [])].sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));

    // consider months up to the exact payment date (not month-end)
    const payDate = new Date(form.payment_date);
    const payCutoff = new Date(payDate.getFullYear(), payDate.getMonth(), payDate.getDate());

    // Build a set of explicitly paid month keys from historical payments (up to payMonthEnd)
    const explicitlyPaid = new Set();
    let latestFullSettlementDate = null;
    for (const p of allPayments) {
      const pDate = new Date(p.payment_date);
      if (pDate > payCutoff) break;
      try {
        const sel = Array.isArray(p.months_selected) ? p.months_selected : JSON.parse(p.months_selected || '[]');
        if (Array.isArray(sel)) sel.forEach((k) => explicitlyPaid.add(String(k)));
      } catch (_) {}
      if (String(p.payment_type) === 'full_settlement') {
        if (!latestFullSettlementDate || pDate > latestFullSettlementDate) latestFullSettlementDate = pDate;
      }
    }

    // Exclude explicit-month payments from the pooled allocation
    const payments = allPayments.filter((p) => {
      const pDate = new Date(p.payment_date);
      if (pDate > payCutoff) return false;
      try {
        const sel = Array.isArray(p.months_selected) ? p.months_selected : JSON.parse(p.months_selected || '[]');
        return !(Array.isArray(sel) && sel.length > 0);
      } catch (_) {
        return true;
      }
    });

    let paidPool = 0;
    let payIdx = 0;
    const result = [];
    for (const m of months) {
      const mDate = new Date(m.calculation_date);
      if (mDate > payCutoff) break;

      while (payIdx < payments.length && new Date(payments[payIdx].payment_date) <= new Date(mDate.getFullYear(), mDate.getMonth() + 1, 0)) {
        paidPool += parseFloat(payments[payIdx].interest_amount || 0);
        payIdx += 1;
      }

      const monthlyInterest = parseFloat(m.interest_amount || 0);
      const key = `${mDate.getFullYear()}-${String(mDate.getMonth() + 1).padStart(2, '0')}`;
      let allocated = 0;
      if (explicitlyPaid.has(key) || (latestFullSettlementDate && new Date(mDate.getFullYear(), mDate.getMonth() + 1, 0) <= latestFullSettlementDate)) {
        allocated = monthlyInterest; // fully paid via explicit selection or full settlement; do not reduce pooled balance
      } else {
        allocated = Math.min(monthlyInterest, paidPool);
        paidPool -= allocated;
        if (paidPool < 0) paidPool = 0;
      }
      const remaining = Number((monthlyInterest - allocated).toFixed(2));
      result.push({
        key,
        date: m.calculation_date,
        interest: Number(monthlyInterest.toFixed(2)),
        remaining
      });
    }
    // Deduplicate by month key (sum amounts) to avoid duplicate visual rows if backend has overlapping entries
    const byKey = new Map();
    for (const row of result) {
      const existing = byKey.get(row.key);
      if (!existing) {
        byKey.set(row.key, { ...row });
      } else {
        existing.interest = Number((existing.interest + row.interest).toFixed(2));
        existing.remaining = Number((existing.remaining + row.remaining).toFixed(2));
      }
    }
    return Array.from(byKey.values());
  }, [loanDetails, form.payment_date]);

  // Fetch outstanding snapshot as of payment_date
  const { data: calcSnap } = useQuery(
    ['loan-interest-snapshot', form.loan_id, form.payment_date],
    () => loanAPI.calculateInterest(form.loan_id, { date: form.payment_date }),
    { enabled: !!form.loan_id && !!form.payment_date }
  );

  // When snapshot is fetched, refresh loans and selected loan details to reflect possible status change (e.g., defaulted)
  useEffect(() => {
    if (!form.loan_id) return;
    if (!calcSnap) return;
    try {
      queryClient.invalidateQueries(['loans']);
      queryClient.invalidateQueries(['loan', String(form.loan_id)]);
      queryClient.invalidateQueries(['loan-details-for-payment', String(form.loan_id)]);
      queryClient.refetchQueries(['loan-details-for-payment', String(form.loan_id)], { exact: true });
    } catch (_) {
      // non-fatal
    }
  }, [calcSnap, form.loan_id, queryClient]);

  // When a loan is selected, force-fetch the latest details and snapshot from the server
  useEffect(() => {
    if (!form.loan_id) return;
    try {
      queryClient.invalidateQueries(['loan-details-for-payment', String(form.loan_id)]);
      queryClient.refetchQueries(['loan-details-for-payment', String(form.loan_id)], { exact: true });
      queryClient.invalidateQueries(['loan-interest-snapshot', form.loan_id, form.payment_date]);
      queryClient.refetchQueries(['loan-interest-snapshot', form.loan_id, form.payment_date], { exact: true });
    } catch (_) {
      // non-fatal
    }
  }, [form.loan_id, form.payment_date, queryClient]);

  const outstanding = useMemo(() => {
    const d = calcSnap?.data?.data;
    return {
      outstanding_principal: Number(d?.outstanding_principal || 0),
      outstanding_interest: Number(d?.outstanding_interest || 0),
      total_outstanding: Number(d?.total_outstanding || 0),
      monthly_interest: Number(d?.monthly_interest || 0)
    };
  }, [calcSnap]);

  // Build a months breakdown for the amount being paid
  const monthsBreakdown = useMemo(() => {
    const list = [];
    if (!form.loan_id) return list;

    // Interest payments: build breakdown from selected months (radio/checkbox)
    if (form.payment_type === 'interest_only' || form.payment_type === 'multiple_months') {
      const unpaid = monthlyUnpaid.filter((m) => m.remaining > 0);
      const selectedSet = new Set(form.selected_month_keys || []);

      if (form.months_option === 'one') {
        // only show when user has explicitly selected a month
        const target = unpaid.find((m) => selectedSet.has(m.key));
        if (target) list.push({ key: target.key, label: formatMonthLabel(target.key, target.date), amount: target.remaining });
      } else {
        // multiple
        unpaid.forEach((m) => {
          if (selectedSet.has(m.key)) {
            list.push({ key: m.key, label: formatMonthLabel(m.key, m.date), amount: m.remaining });
          }
        });
      }
    }

    if (form.payment_type === 'full_settlement') {
      if (outstanding.outstanding_interest > 0) list.push({ key: 'interest', label: 'Interest Due', amount: outstanding.outstanding_interest });
      if (outstanding.outstanding_principal > 0) list.push({ key: 'principal', label: 'Principal', amount: outstanding.outstanding_principal });
    }

    if (form.payment_type === 'partial_principal') {
      if (outstanding.outstanding_interest > 0) list.push({ key: 'interest', label: 'Interest Due', amount: outstanding.outstanding_interest });
    }

    return list;
  }, [form.loan_id, form.payment_type, form.months_paid, form.payment_date, form.months_option, form.selected_month_keys, monthlyUnpaid, outstanding]);

  // Auto-calculate suggested amount when relevant inputs change
  useEffect(() => {
    if (!form.loan_id) return;
    let suggested = 0;

    const monthly = Number(outstanding.monthly_interest || 0);
    const oi = Number(outstanding.outstanding_interest || 0);
    const op = Number(outstanding.outstanding_principal || 0);

    if (form.payment_type === 'interest_only' || form.payment_type === 'multiple_months') {
      const unpaid = monthlyUnpaid.filter((m) => m.remaining > 0);
      const selectedSet = new Set(form.selected_month_keys || []);
      if (form.months_option === 'one') {
        const target = unpaid.find((m) => selectedSet.has(m.key));
        suggested = target ? target.remaining : 0;
      } else {
        suggested = unpaid.reduce((sum, r) => sum + (selectedSet.has(r.key) ? r.remaining : 0), 0);
      }
    } else if (form.payment_type === 'partial_principal') {
      suggested = oi; // must clear interest first; user may increase to reduce principal
    } else if (form.payment_type === 'full_settlement') {
      suggested = oi + op;
    }

    const normalized = Number.isFinite(suggested) ? Number(suggested.toFixed(2)) : 0;
    setForm((prev) => ({ ...prev, amount: normalized }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.loan_id, form.payment_type, form.months_paid, form.payment_date, form.months_option, form.selected_month_keys, monthlyUnpaid, outstanding]);

  // If user selects partial principal while interest is outstanding, auto-switch to interest flow
  useEffect(() => {
    if (!form.loan_id) return;
    if (form.payment_type !== 'partial_principal') return;
    const oi = Number(outstanding.outstanding_interest || 0);
    if (oi <= 0) return;
    // Find earliest unpaid month and switch user to interest payment for that month
    const unpaid = monthlyUnpaid.filter((m) => m.remaining > 0);
    if (unpaid.length === 0) return;
    const firstUnpaid = unpaid[0];
    setForm((prev) => ({
      ...prev,
      payment_type: 'interest_only',
      months_option: 'one',
      selected_month_keys: [firstUnpaid.key],
      amount: Number(firstUnpaid.remaining || 0)
    }));
    toast.info('Interest is outstanding. Switched to interest payment for the earliest pending month.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.payment_type, form.loan_id, outstanding, monthlyUnpaid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Reset dependent selections when payment type or months option changes
    if (name === 'payment_type') {
      setForm((prev) => ({ ...prev, payment_type: value, selected_month_keys: [] }));
      return;
    }
    if (name === 'months_option') {
      setForm((prev) => ({ ...prev, months_option: value, selected_month_keys: [] }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.loan_id) return;
    if (isLoanClosed) {
      toast.error('This loan is closed. Further payments are not allowed.');
      return;
    }
    // Enforce selection of months for interest payments so months_selected is always populated
    if (form.payment_type === 'interest_only') {
      if (form.months_option === 'one' && (!form.selected_month_keys || form.selected_month_keys.length !== 1)) {
        toast.error('Please select exactly one remaining month');
        return;
      }
      if (form.months_option === 'multiple' && (!form.selected_month_keys || form.selected_month_keys.length === 0)) {
        toast.error('Please select one or more remaining months');
        return;
      }
    }
    const isInterest = form.payment_type === 'interest_only';
    const isMultipleMonths = isInterest && form.months_option === 'multiple';
    const payload = {
      loan_id: Number(form.loan_id),
      payment_type: isMultipleMonths ? 'multiple_months' : form.payment_type,
      amount: Number(form.amount),
      principal_amount: 0,
      interest_amount: 0,
      months_paid:
        isInterest
          ? (form.months_option === 'one' ? 1 : Math.max(1, (form.selected_month_keys || []).length))
          : 1,
      months_selected:
        isInterest
          ? (form.months_option === 'one' ? (form.selected_month_keys || []).slice(0, 1) : (form.selected_month_keys || []))
          : [],
      payment_date: form.payment_date,
      payment_method: form.payment_method,
      remarks: form.remarks
    };
    try {
      const res = await createPayment.mutateAsync(payload);
      const created = res?.data?.data;
      toast.success('Payment processed successfully');
      // Refresh relevant lists/caches so amounts (partial/full) reflect the new payment immediately
      queryClient.invalidateQueries(['payments']);
      queryClient.invalidateQueries(['loans']);
      queryClient.invalidateQueries(['loan', String(form.loan_id)]);
      queryClient.invalidateQueries(['loan-details-for-payment', String(form.loan_id)]);
      // Invalidate all snapshots for this loan regardless of date (predicate match)
      queryClient.invalidateQueries({
        predicate: (q) => q.queryKey && q.queryKey[0] === 'loan-interest-snapshot' && String(q.queryKey[1]) === String(form.loan_id)
      });
      queryClient.invalidateQueries(['loan-upcoming-interest', String(form.loan_id)]);
      if (created?.id) {
        navigate(`/payments/${created.id}`);
      } else {
        navigate('/payments');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to process payment';
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-primary-700">Process Payment</h1>
        <p className="mt-1 text-sm text-gray-500">Create a new payment for a loan</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Loan selector */}
        <div className="lg:col-span-1 bg-white p-4 rounded shadow">
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700">Search Loans</label>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              placeholder="Search by loan/customer"
              onChange={(e) => setLoanQuery((p) => ({ ...p, search: e.target.value, page: 1 }))}
            />
          </div>
          <div className="overflow-y-auto max-h-80 divide-y">
            {loansLoading ? (
              <div className="py-6 flex justify-center"><LoadingSpinner size="sm" /></div>
            ) : (
              loanList.map((l) => {
                const disabled = l.status === 'closed';
                return (
                  <button
                    key={l.id}
                    type="button"
          onClick={() => !disabled && setForm((prev) => ({ ...prev, loan_id: l.id, selected_month_keys: [] }))}
                    className={`w-full text-left px-3 py-2 hover:bg-primary-50 ${String(form.loan_id) === String(l.id) ? 'bg-primary-50' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                    title={disabled ? 'Loan not active' : ''}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900">{l.loan_number}</div>
                      <span className={`text-2xs px-2 py-0.5 rounded ${l.status === 'active' ? 'bg-green-100 text-green-700' : l.status === 'defaulted' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{l.status}</span>
                    </div>
                    <div className="text-xs text-gray-500">{l.first_name} {l.last_name}</div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Payment form */}
        <form className="lg:col-span-2 bg-white p-6 rounded shadow space-y-4" onSubmit={handleSubmit}>
          {isLoanClosed && (
            <div className="mb-2 p-3 rounded bg-yellow-50 text-yellow-800 text-sm">
              This loan is already closed via full settlement. New payments cannot be processed.
            </div>
          )}
          {/* Read-only customer info */}
          {selectedLoan && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-500">Customer</div>
                <div className="text-lg font-semibold">{selectedLoan.first_name} {selectedLoan.last_name}</div>
              </div>
              <div>
                <div className="text-sm text-gray-500">Mobile</div>
                <div className="text-lg font-semibold">{selectedLoan.mobile_1}</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Selected Loan</label>
              <input
                readOnly
                className="mt-1 w-full border rounded px-3 py-2 bg-primary-50"
                value={selectedLoan ? `${selectedLoan.loan_number} — ${formatCurrency(selectedLoan.loan_amount)}` : ''}
                placeholder="Choose a loan from the left"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Date</label>
              <input
                type="date"
                name="payment_date"
                value={form.payment_date}
                onChange={handleChange}
                className="mt-1 w-full border rounded px-3 py-2"
                disabled={isLoanClosed}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Payment Type</label>
              <select
                name="payment_type"
                value={form.payment_type}
                onChange={handleChange}
                className="mt-1 w-full border rounded px-3 py-2"
                disabled={isLoanClosed}
              >
                <option value="interest_only">Interest</option>
                <option value="partial_principal">Partial payment</option>
                <option value="full_settlement">Full payment</option>
              </select>
            </div>
            {form.payment_type === 'interest_only' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Interest Coverage</label>
                <select
                  name="months_option"
                  value={form.months_option}
                  onChange={handleChange}
                  className="mt-1 w-full border rounded px-3 py-2"
                  disabled={isLoanClosed}
                >
                  <option value="one">1 Month</option>
                  <option value="multiple">Multiple months</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">Months are counted from loan creation month up to the selected payment month.</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                className="mt-1 w-full border rounded px-3 py-2"
                disabled={isLoanClosed}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Method</label>
              <select
                name="payment_method"
                value={form.payment_method}
                onChange={handleChange}
                className="mt-1 w-full border rounded px-3 py-2"
                disabled={isLoanClosed}
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Remarks</label>
              <textarea
                name="remarks"
                rows="3"
                value={form.remarks}
                onChange={handleChange}
                className="mt-1 w-full border rounded px-3 py-2"
                disabled={isLoanClosed}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Months breakdown */}
          {form.payment_type === 'interest_only' && (
            <div className="mt-2 border rounded p-3 bg-primary-50">
              <div className="text-sm font-medium text-gray-700 mb-2">Select Remaining Months</div>
              <div className="space-y-2">
                {(() => {
                  const unpaid = monthlyUnpaid.filter((m) => m.remaining > 0);
                  if (unpaid.length === 0) return <div className="text-sm text-gray-500">No pending months as of selected date.</div>;
                  if (form.months_option === 'one') {
                    return (
                      <div className="space-y-2">
                        {unpaid.map((m) => (
                          <label key={m.key} className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="single_month"
                                checked={(form.selected_month_keys || []).includes(m.key)}
                                onChange={() => setForm((p) => ({ ...p, selected_month_keys: [m.key] }))}
                                disabled={isLoanClosed}
                              />
                              <span>{new Date(m.date).toLocaleString('en-US', { month: 'short', year: 'numeric' })}</span>
                            </span>
                            <span>{formatCurrency(m.remaining)}</span>
                          </label>
                        ))}
                      </div>
                    );
                  }
                  // multiple
                  return (
                    <div className="space-y-2">
                      {unpaid.map((m) => (
                        <label key={m.key} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={(form.selected_month_keys || []).includes(m.key)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setForm((p) => {
                                  const set = new Set(p.selected_month_keys || []);
                                  if (checked) set.add(m.key); else set.delete(m.key);
                                  return { ...p, selected_month_keys: Array.from(set) };
                                });
                              }}
                              disabled={isLoanClosed}
                            />
                            <span>{new Date(m.date).toLocaleString('en-US', { month: 'short', year: 'numeric' })}</span>
                          </span>
                          <span>{formatCurrency(m.remaining)}</span>
                        </label>
                      ))}
                    </div>
                  );
                })()}
              </div>
              {monthsBreakdown.length > 0 && (
                <>
                  <div className="mt-3 text-sm font-medium text-gray-700">Selected Months</div>
                  <div className="space-y-1 mt-1">
                    {monthsBreakdown.map((m) => (
                      <div key={m.key} className="flex justify-between text-sm">
                        <span>{m.label}</span>
                        <span>{formatCurrency(m.amount)}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
              <div className="mt-2 pt-2 border-t flex justify-between text-sm font-semibold">
                <span>Total</span>
                <span>{formatCurrency(Number(form.amount || 0))}</span>
              </div>
            </div>
          )}

          <div className="pt-4">
            <button
              type="submit"
              disabled={createPayment.isLoading || !form.loan_id || isLoanClosed}
              className="btn btn-md btn-primary rounded disabled:opacity-50"
            >
              {createPayment.isLoading ? 'Processing...' : 'Submit Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePayment;


